/**
 * The AAL2 read-gate state for the My Pay screen (C7 PR B, #21938) —
 * rendered when `use_my_pay.ts`'s `state.kind === 'step_up_required'`.
 *
 * Deliberately NOT a reuse of `components/auth/step_up_dialog.tsx`:
 *
 *  1. `StepUpDialog` is a dismissable `Dialog` overlay for a WRITE action
 *     the user just attempted — there is nothing to "overlay" here; this
 *     state IS the screen's entire content while it's active.
 *  2. Its copy (`wp.auth.step_up.description`) is write-path-specific
 *     ("this action requires…") and isn't adaptable to a read gate without
 *     becoming misleading — see the separate `wp.my_pay.step_up.*` keys.
 *  3. "Sign in again" is `StepUpDialog`'s own KNOWN, unwired gap (#22046,
 *     `signOut()` alone doesn't touch the still-live ledger-web session)
 *     but is the WORKING completion of the enrol-2FA-then-retry loop here
 *     (`lib/auth/step_up.ts`'s module docblock) — coupling the two would
 *     tie a broken button and a working one together for no reason.
 *
 * Only the shared MECHANICS are generalized into `lib/auth/step_up.ts`
 * (`restartSignIn`); the presentation is intentionally duplicated, mirroring
 * `apps/tax-web/src/screens/ess_accept_screen.tsx`'s `state.kind ===
 * 'step_up'` branch.
 *
 * ── No "set up 2FA" link, deliberately ─────────────────────────────────────
 *
 * This panel used to lead with a CTA to `${resolveLedgerWebUrl()}
 * /settings/security` — sending a WagePro employee to the LEDGER dashboard to
 * enrol 2FA before they could look at their own payslip. WagePro is its own
 * product (CLAUDE.md § Branding) and has no TOTP-enrolment screen of its own,
 * so that link was the only route out of this state and it left the product.
 * It is gone, along with the requirement that produced it:
 * `requireAal2ExceptWageproSelfService` (`apps/api/src/trpc/init.ts`) now
 * permanently exempts the three payslip READ procedures for first-party
 * WagePro callers, so this panel is unreachable for them.
 *
 * It is KEPT rather than deleted because the state is still reachable for a
 * non-WagePro caller of the same procedures (apps/tax-web's My Pay screens,
 * which retain their step-up by design), and because a UI should not render
 * blank if the server ever gates a read again. What it must never do is point
 * at another product — hence "try again" is now the only action, and the copy
 * tells the reader to contact their administrator instead.
 */
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard } from '@jumpstone/ui-react';
import { restartSignIn } from '../../lib/auth/step_up.js';

export function MyPayStepUpPanel(): React.JSX.Element {
  const { t } = useTranslation('wp');

  return (
    <GlassCard variant="accent" className="rounded-2xl p-5">
      <h2 className="font-bold mb-1.5">{t('my_pay.step_up.title')}</h2>
      <p className="text-[13.5px] text-[color:var(--ink-muted)] mb-3.5">
        {t('my_pay.step_up.description')}
      </p>
      <div className="flex gap-2">
        <Button
          variant="primary"
          onClick={() => {
            // A plain re-query can't fix an AAL1 token -- only a fresh
            // authorize round trip observes an upgraded session (see
            // lib/auth/step_up.ts's module docblock and restartSignIn's own
            // doc comment for the full trace, including the residual
            // refresh-rotation gap this does NOT fix).
            restartSignIn();
          }}
        >
          {t('my_pay.step_up.retry_cta')}
        </Button>
      </div>
    </GlassCard>
  );
}
